import { Button, FormField } from '@beatq/ui';
import { PinPad } from './PinPad.js';
import { pinIsComplete } from './pinPadLogic.js';
import { staffRoleLabel } from './catalog.js';
import type { StaffMember } from './staffPinApi.js';

function staffLabel(member: StaffMember) {
  return (
    member.displayName ??
    member.email ??
    staffRoleLabel(member.roleCode) ??
    member.id ??
    'Staff member'
  );
}

export function PinSignInForm({
  staff,
  staffId,
  pin,
  onStaffId,
  onPin,
  onSubmit,
  pending,
  missingBranch,
  errorMessage,
  emptyMessage = 'No staff available for PIN sign-in.',
  idPrefix = 'pin',
}: {
  staff: StaffMember[];
  staffId: string;
  pin: string;
  onStaffId: (id: string) => void;
  onPin: (pin: string) => void;
  onSubmit: () => void;
  pending: boolean;
  missingBranch: boolean;
  errorMessage?: string | null;
  emptyMessage?: string;
  idPrefix?: string;
}) {
  const staffFieldId = `${idPrefix}Staff`;
  const pinFieldId = `${idPrefix}Code`;
  return (
    <form
      className="mer-detail-form"
      data-testid="merchant-pin-sign-in-form"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      {missingBranch ? (
        <p role="alert" className="m-0 text-danger">
          Branch context required. Select a branch before PIN sign-in.
        </p>
      ) : null}
      {errorMessage ? (
        <p role="alert" className="m-0 text-danger">
          {errorMessage}
        </p>
      ) : null}
      {staff.length === 0 ? (
        <p role="status" className="mer-detail-list-meta m-0">
          {emptyMessage}
        </p>
      ) : null}
      <FormField label="Staff member" htmlFor={staffFieldId} required>
        <select
          id={staffFieldId}
          value={staffId}
          onChange={(e) => onStaffId(e.target.value)}
          className="min-h-target w-full rounded-control border border-line bg-raised px-field"
          disabled={pending || staff.length === 0}
        >
          <option value="">Select staff</option>
          {staff.map((member) => {
            const accountId = member.accountId;
            if (!accountId) return null;
            return (
              <option key={accountId} value={accountId}>
                {staffLabel(member)}
              </option>
            );
          })}
        </select>
      </FormField>
      <PinPad id={pinFieldId} value={pin} onChange={onPin} disabled={pending} />
      <div className="mer-detail-form-actions">
        <Button
          type="submit"
          disabled={pending || !staffId || !pinIsComplete(pin) || missingBranch}
        >
          {pending ? 'Signing in…' : 'Sign in'}
        </Button>
      </div>
    </form>
  );
}
